import { useQuery } from '@tanstack/react-query';
import { FileDownIcon, FileUpIcon, LockIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { DocumentEditor } from '../../../editor/components/documentEditor.component';
import { useAiDisclaimer } from '../../../editor/hooks/useAiDisclaimer.hook';
import { getDocumentStructure } from '../../../editor/hooks/useDocumentStructure';
import { useYjsProvider } from '../../../editor/hooks/useYjsProvider.hook';
import { getRandomCursorColor } from '../../../editor/utils/editor.utils';
import { getDocumentData, getDocumentExportRequestData } from '../../../editor/utils/export.utils';
import { getNameInitials } from '../../../editor/utils/string.utils';
import { useModals } from '../../../hooks/useModals.hook';
import { setReturnPath as setCreditTopUpReturnPath } from '../../../lib/creditTopUpReturn.utils';
import { downloadFile } from '../../../lib/file.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { AppHeaderButton } from '../../components/appHeader/appHeaderButton.component';
import { DocumentSyncStatus } from '../../components/document/documentSyncStatus.component';
import { useCredits } from '../../hooks/useCredits.hook';
import { useSession } from '../../hooks/useSession.hook';
import { AiProofreadModal } from '../../modals/aiProofread/aiProofreadModal.component';
import { ExportModal } from '../../modals/export/exportModal.component';
import { DocumentHelpModal } from '../../modals/feedback/documentHelpModal.component';
//import { DocumentShareModal } from '../../modals/share/documentShareModal.component';
import { RouteIndex } from '../../routeIndex';
import { DocumentPayment } from './documentPayment.component';
import { DocumentSettings } from './documentSettings.component';

const EditDocumentView = () => {
    const api = useApi();
    const modals = useModals();
    const { useAiFeature } = useAiDisclaimer();
    const { identity } = useSession();

    const { documentId, revisionId } = useParams<{ documentId: string, revisionId?: string }>();

    const { data: document, isLoading } = useQuery({
        queryKey: ['document', documentId],
        queryFn: async () => {
            return api.v1.documents.getById(documentId ?? '');
        }
    });

    const currentRevisionId = revisionId ?? document?.currentRevisionId;

    const getAccessToken = useCallback(() => {
        return api.client.getAccessToken() ?? 'not-provided';
    }, [ api ]);

    const yjsProvider = useYjsProvider({
        url: import.meta.env.VITE_WS_URL ?? (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws/',
        name: document
            ? `${document.documentId}/${currentRevisionId}`
            : '',
        token: getAccessToken
    });

    const importYjsProvider = useYjsProvider({
        url: import.meta.env.VITE_WS_URL ?? (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws/',
        name: document?.paidForAt
            ? `${document.documentId}/import`
            : '',
        token: getAccessToken
    });

    const onDocumentExport = useCallback(() => {
        if (!document?.template || !yjsProvider.provider) {
            return;
        }

        const data = getDocumentData(document.template, yjsProvider.provider.document);

        modals.showModal('doc-export', ExportModal, {
            config: document.template.config,
            data: data,
            documentType: document.documentType,
            documentName: document.documentName,
        });
    }, [documentId, document, yjsProvider]);

    const onAiProofread = useCallback(() => {
        useAiFeature(() => {
            const docStructure = getDocumentStructure(document!.template!, yjsProvider.provider!.document);

            modals.showModal('ai-proofread', AiProofreadModal, {
                document: document!,
                documentStructure: docStructure
            });
        });
    }, [ document, yjsProvider ]);

    /* const onDocumentShare = useCallback(() => {
        modals.showModal('doc-share', DocumentShareModal, {
            documentId: documentId!
        });
    }, [documentId]); */

    const onRequestHelp = useCallback((section?: string, fields?: Record<string, string>) => {
        modals.showModal('doc-help', DocumentHelpModal, {
            documentId: documentId!,
            documentSection: section,
            fields
        });
    }, [ documentId ]);

    const onDownloadJson = useCallback(() => {
        if (!document?.template || !yjsProvider.provider) {
            return;
        }
        const docData = getDocumentData(document.template, yjsProvider.provider.document);
        const exportData = getDocumentExportRequestData(document.template.config, docData, document.documentType, document.documentName);
        downloadFile(JSON.stringify(exportData, null, 2), 'document.json', 'application/json');
    }, [ document, yjsProvider ]);

    const renderSettings = useCallback((className?: string) => {
        const canDownloadJson = !!document?.template && !!yjsProvider.provider;
        return (
            <DocumentSettings
                documentId={documentId!}
                className={className}
                onDownloadJson={canDownloadJson ? onDownloadJson : undefined}
            />
        );
    }, [ documentId, document, yjsProvider, onDownloadJson ]);

    const renderUnlock = useCallback(() => {
        return <DocumentPayment documentId={documentId!} />;
    }, [ documentId ]);

    const cursorData = useMemo(() => {
        return {
            identityId: identity.identityId,
            name: identity.name,
            initials: getNameInitials(identity.name),
            color: getRandomCursorColor()
        };
    }, [ identity.identityId, identity.name ]);

    const isUnlocked = !!document?.paidForAt;

    const { balance } = useCredits();
    const navigate = useNavigate();

    return (
        <>
            <AppHeader
                pageTitle={'Úprava dokumentu'}
                backLink={RouteIndex.documentList}
                leftSlot={() => (
                    <DocumentSyncStatus
                        provider={yjsProvider}
                        className='hidden lg:flex flex-shrink overflow-hidden'
                        labelClass='hidden xl:inline-block'
                    />
                )}
            >
                {isUnlocked && (
                    <AppHeaderButton
                        style='primary'
                        size='lg'
                        className='hidden md:flex leading-[15px]'
                        onClick={() => {
                            setCreditTopUpReturnPath(window.location.pathname + window.location.search + window.location.hash);
                            navigate(pathWithParams(RouteIndex.topUpCredit, {}));
                        }}
                    >
                        <PlusIcon size={18} />
                        <span className='hidden xl:block'>Dobít kredit <br/> <small>(současné: {balance})</small></span>
                    </AppHeaderButton>
                )}
                <AppHeaderButton
                    style='feature-accent'
                    size='lg'
                    disabled={!isUnlocked}
                    onClick={onAiProofread}
                    className='hidden md:flex'
                    tooltip='AI korektura dokumentu'
                >
                    {isUnlocked && (
                        <SparklesIcon size={18} className="relative" />
                    )}
                    {!isUnlocked && (
                        <LockIcon size={18} className="relative" />
                    )}
                </AppHeaderButton>
                {isUnlocked && (
                    <AppHeaderButton
                        style='default'
                        size='lg'
                        onClick={() => { window.location.hash = '#import'; }}
                        className='hidden md:flex border-2 border-violet-500 text-violet-700'
                        tooltip='Nahrát práci'
                    >
                        <FileUpIcon size={18} />
                    </AppHeaderButton>
                )}
                <AppHeaderButton
                    style='primary'
                    size='lg'
                    disabled={!isUnlocked}
                    onClick={onDocumentExport}
                    className='hidden md:flex'
                    tooltip='Stáhnout dokument'
                >
                    {isUnlocked && (
                        <FileDownIcon size={18} />
                    )}
                    {!isUnlocked && (
                        <LockIcon size={18} />
                    )}
                </AppHeaderButton>
                {/*<AppHeaderButton style='default' size='lg' onClick={onDocumentShare} tooltip='Sdílet'>
                    <UsersIcon size={20} />
                </AppHeaderButton>*/}
            </AppHeader>

            {!isLoading && document && !document.template && (
                <div className="flex flex-col justify-start items-center min-h-screen pt-14">
                    {renderSettings('p-4 md:p-8 lg:py-12 w-[36rem] max-w-full')}
                </div>
            )}

            {!isLoading && document && document.template && (
                <DocumentEditor
                    template={document.template}
                    document={document}
                    currentRevisionId={currentRevisionId!}
                    yjsProviderState={yjsProvider}
                    importYjsProviderState={importYjsProvider}
                    cursorData={cursorData}
                    renderSettings={renderSettings}
                    renderUnlock={renderUnlock}
                    onDocumentExport={onDocumentExport}
                    onRequestHelp={onRequestHelp}
                />
            )}
        </>
    );
};

export default EditDocumentView;
